import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { setViewport } from '@actions/map-actions';
import Components from './components';
import DotMapsGoogleMap from '@shared/dotmaps-google-map';
import FloatingBar from '@components/map/floating-bar';
import LayersMenu from '@components/map/layers-menu';
import { segmentsToBounds } from '@utils/map-utils';
import './group-map.scss';

const Map = () => {
  const dispatch = useDispatch();

  const boundsData = useSelector(state => state.map.boundsData);

  const [fitBound, setFitBound] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);

  // Store ref for the debounced function to access current mapInstance
  const mapInstanceRef = useRef(mapInstance);
  mapInstanceRef.current = mapInstance;

  // Create debounced updateViewport function
  const updateViewport = useMemo(
    () => debounce(() => {
      if (mapInstanceRef.current) {
        dispatch(setViewport('group', mapInstanceRef.current));
      }
    }, 600),
    [dispatch]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      updateViewport.cancel();
    };
  }, [updateViewport]);

  // Fit bounds when boundsData is available and we haven't fit yet
  useEffect(() => {
    if (mapInstance && boundsData && !fitBound) {
      const bounds = segmentsToBounds(boundsData.segments);
      if (bounds) {
        mapInstance.fitBounds(bounds);
        // For the group map, only zoom it when loading it the first time.
        // We must not zoom to fit the shape when we are editing it, since
        // if we increase the shape area, we'll be zooming to the original
        // shape extent.
        setFitBound(true);
      }
    }
  }, [boundsData, fitBound, mapInstance]);

  const onMapLoad = useCallback(instance => {
    if (instance) {
      setMapInstance(instance);
      window.mapInstance = instance;
    }
  }, []);

  return (
    <div styleName="map-container">
      {mapInstance && <LayersMenu style={{ right: '1rem' }} />}
      {mapInstance && <FloatingBar legend={false} tools={false} />}
      <DotMapsGoogleMap
        components={<Components />}
        onBoundsChanged={updateViewport}
        onMapLoad={onMapLoad}
      />
    </div>
  );
};

export default Map;
